import { useState } from "react";
import { setOnboardingComplete, saveOnboardingData } from "../lib/userDataService";

// Onboarding steps configuration
const STEPS = [
  {
    id: 'welcome',
    title: "Welcome to PCOS Insights",
    subtitle: "Let's personalize your experience with a few quick questions. This helps us give you more relevant insights.",
    type: 'intro'
  },
  {
    id: 'age',
    title: "How old are you?",
    subtitle: "Your age helps us provide age-appropriate health insights.",
    type: 'age_input'
  },
  {
    id: 'diagnosis',
    title: "Have you been diagnosed with PCOS?",
    subtitle: "This helps us tailor content to where you are in your journey.",
    type: 'single_select',
    options: [
      { value: 'diagnosed', label: 'Yes, I have a diagnosis', icon: '✓' },
      { value: 'suspected', label: 'I suspect I might have it', icon: '?' },
      { value: 'exploring', label: "I'm exploring my symptoms", icon: '🔍' },
      { value: 'managing', label: 'Managing symptoms, no formal diagnosis', icon: '💪' }
    ]
  },
  {
    id: 'cycle',
    title: "How would you describe your cycles?",
    subtitle: "Understanding your cycle pattern helps us personalize tracking.",
    type: 'single_select',
    options: [
      { value: 'regular', label: 'Regular (21-35 days)', icon: '📅' },
      { value: 'irregular', label: 'Irregular (varies a lot)', icon: '🔄' },
      { value: 'long', label: 'Long cycles (35+ days)', icon: '⏳' },
      { value: 'absent', label: 'Absent or very rare', icon: '—' },
      { value: 'unsure', label: "I'm not sure", icon: '❓' }
    ]
  },
  {
    id: 'goal',
    title: "What's your primary health goal?",
    subtitle: "We'll prioritize insights and tips based on what matters most to you.",
    type: 'single_select',
    options: [
      { value: 'periods', label: 'Regulate my periods', icon: '🩸' },
      { value: 'weight', label: 'Manage my weight', icon: '⚖️' },
      { value: 'skin_hair', label: 'Improve skin & hair', icon: '✨' },
      { value: 'fertility', label: 'Support fertility', icon: '🌸' },
      { value: 'general', label: 'Overall health & wellness', icon: '💚' }
    ]
  }
];

export default function OnboardingScreen({ userId, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    age: '',
    diagnosis_status: '',
    cycle_regularity: '',
    primary_goal: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleOptionSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleNext = async () => {
    // Validate current step
    if (step.id === 'age' && (!formData.age || formData.age < 13 || formData.age > 99)) {
      setError('Please enter a valid age (13-99)');
      return;
    }
    if (step.id === 'diagnosis' && !formData.diagnosis_status) {
      setError('Please select an option');
      return;
    }
    if (step.id === 'cycle' && !formData.cycle_regularity) {
      setError('Please select an option');
      return;
    }
    if (step.id === 'goal' && !formData.primary_goal) {
      setError('Please select an option');
      return;
    }

    if (isLastStep) {
      // Save to user-specific storage and complete
      await saveOnboardingDataToStorage();
    } else {
      setCurrentStep(prev => prev + 1);
      setError('');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setError('');
    }
  };

  const saveOnboardingDataToStorage = async () => {
    setSaving(true);
    setError('');

    try {
      // Save onboarding data to Supabase profiles table (sets onboarding_completed=true)
      await saveOnboardingData(userId, {
        age: formData.age,
        diagnosis_status: formData.diagnosis_status,
        cycle_regularity: formData.cycle_regularity,
        primary_goal: formData.primary_goal
      });

      // Notify parent component
      onComplete(formData);
    } catch (err) {
      console.error('Error saving onboarding data:', err);
      // Still try to mark complete and proceed
      await setOnboardingComplete(userId);
      onComplete(formData);
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    if (step.type === 'intro') return true;
    if (step.id === 'age') return formData.age && formData.age >= 13 && formData.age <= 99;
    if (step.id === 'diagnosis') return !!formData.diagnosis_status;
    if (step.id === 'cycle') return !!formData.cycle_regularity;
    if (step.id === 'goal') return !!formData.primary_goal;
    return true;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F6FB 0%, #EDE9FE 100%)',
      padding: '24px 20px',
      display: 'flex',
      flexDirection: 'column'
    }} data-testid="onboarding-screen">
      
      {/* Progress Bar */}
      <div style={{
        width: '100%',
        height: 4,
        background: '#E5E7EB',
        borderRadius: 2,
        marginBottom: 32,
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          background: 'linear-gradient(90deg, #A78BFA, #6D5EF5)',
          borderRadius: 2,
          transition: 'width 0.3s ease'
        }} />
      </div>

      {/* Step Counter */}
      <div style={{
        fontSize: 12,
        color: '#8A8A9A',
        marginBottom: 8,
        textTransform: 'uppercase',
        letterSpacing: '0.1em'
      }}>
        Step {currentStep + 1} of {STEPS.length}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        
        {/* Title & Subtitle */}
        <h1 style={{
          fontSize: 24,
          fontWeight: 700,
          color: '#2F2F35',
          marginBottom: 8,
          lineHeight: 1.3
        }}>
          {step.title}
        </h1>
        <p style={{
          fontSize: 14,
          color: '#6B7280',
          lineHeight: 1.6,
          marginBottom: 32
        }}>
          {step.subtitle}
        </p>

        {/* Welcome Step - Intro */}
        {step.type === 'intro' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
            marginTop: 20
          }}>
            <div style={{
              width: 100,
              height: 100,
              borderRadius: 30,
              background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(109,94,245,0.3)'
            }}>
              <span style={{ fontSize: 40 }}>💜</span>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, margin: 0, textAlign: 'center' }}>
                We're here to help you understand your body better. Your answers stay private and help us show you the most relevant insights.
              </p>
            </div>
          </div>
        )}

        {/* Age Input Step */}
        {step.type === 'age_input' && (
          <div style={{ marginTop: 20 }}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: '20px 24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}>
              <label style={{ 
                display: 'block', 
                fontSize: 13, 
                fontWeight: 600, 
                color: '#6B7280',
                marginBottom: 12 
              }}>
                Your Age
              </label>
              <input
                type="number"
                min="13"
                max="99"
                value={formData.age}
                onChange={(e) => handleOptionSelect('age', e.target.value)}
                placeholder="e.g., 28"
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: 24,
                  fontWeight: 700,
                  border: '2px solid #E5E7EB',
                  borderRadius: 12,
                  background: '#F9FAFB',
                  color: '#2F2F35',
                  textAlign: 'center',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#A78BFA'}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                data-testid="onboarding-age-input"
              />
              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 12, textAlign: 'center' }}>
                Ages 13-99
              </p>
            </div>
          </div>
        )}

        {/* Single Select Options */}
        {step.type === 'single_select' && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 12,
            marginTop: 8
          }}>
            {step.options.map((option) => {
              const fieldMap = {
                diagnosis: 'diagnosis_status',
                cycle: 'cycle_regularity',
                goal: 'primary_goal'
              };
              const field = fieldMap[step.id];
              const isSelected = formData[field] === option.value;
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect(field, option.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '16px 20px',
                    background: isSelected 
                      ? 'linear-gradient(135deg, rgba(167,139,250,0.15), rgba(109,94,245,0.15))' 
                      : '#fff',
                    border: isSelected 
                      ? '2px solid #A78BFA' 
                      : '2px solid #E5E7EB',
                    borderRadius: 14,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    boxShadow: isSelected 
                      ? '0 4px 16px rgba(109,94,245,0.15)' 
                      : '0 2px 8px rgba(0,0,0,0.03)'
                  }}
                  data-testid={`onboarding-option-${option.value}`}
                >
                  <span style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: isSelected 
                      ? 'linear-gradient(135deg, #A78BFA, #6D5EF5)' 
                      : '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    flexShrink: 0
                  }}>
                    {option.icon}
                  </span>
                  <span style={{
                    fontSize: 15,
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? '#5B21B6' : '#374151'
                  }}>
                    {option.label}
                  </span>
                  {isSelected && (
                    <span style={{
                      marginLeft: 'auto',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 10,
            color: '#991B1B',
            fontSize: 13
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginTop: 32,
        paddingBottom: 20
      }}>
        {currentStep > 0 && (
          <button
            onClick={handleBack}
            style={{
              flex: 1,
              padding: '16px 24px',
              fontSize: 15,
              fontWeight: 600,
              background: '#fff',
              border: '2px solid #E5E7EB',
              borderRadius: 14,
              color: '#6B7280',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="onboarding-back-btn"
          >
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={!canProceed() || saving}
          style={{
            flex: currentStep > 0 ? 2 : 1,
            padding: '16px 24px',
            fontSize: 15,
            fontWeight: 600,
            background: canProceed() 
              ? 'linear-gradient(135deg, #A78BFA, #6D5EF5)' 
              : '#E5E7EB',
            border: 'none',
            borderRadius: 14,
            color: canProceed() ? '#fff' : '#9CA3AF',
            cursor: canProceed() ? 'pointer' : 'not-allowed',
            boxShadow: canProceed() ? '0 4px 16px rgba(109,94,245,0.3)' : 'none',
            transition: 'all 0.2s ease',
            opacity: saving ? 0.7 : 1
          }}
          data-testid="onboarding-next-btn"
        >
          {saving ? 'Saving...' : isLastStep ? "Let's Go!" : 'Continue'}
        </button>
      </div>

      {/* Skip option on first step */}
      {currentStep === 0 && (
        <button
          onClick={() => {
            setOnboardingComplete(userId);
            onComplete(null);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#8A8A9A',
            fontSize: 13,
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '8px 0'
          }}
          data-testid="onboarding-skip-btn"
        >
          Skip for now
        </button>
      )}
    </div>
  );
}
